import { createFile } from '@/api/fileUpload';
import { useCreateCampaign } from '@/api/hooks/b2bFinance/useCampaign';
import { useGetCompanies } from '@/api/hooks/company/useCompany';
import axios from 'axios';
import { Options } from '@/components/Common/Selects/Select.types';
import {
  CampaignFormValues,
  campaignValidationSchema,
} from '@/hooks/useCampaignForm';
import { useState } from 'react';
import { CampaignType } from '@/api/gql/graphql';
import { useCampaignStore } from '@/store/CampaignStore/CampaignStore';
import { useToast } from '@/hooks/useToast';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useFormatDate } from '@/hooks/useFormatDate';

export enum BannerType {
  WELCOME_POPUP = 'WELCOME_POPUP',
  MAIN_BANNER = 'MAIN_BANNER',
  AUDIO_PRE_LESSON = 'AUDIO_PRE_LESSON',
  TEXT_LESSON_POPUP = 'TEXT_LESSON_POPUP',
  TEXT_LESSON_BANNER = 'TEXT_LESSON_BANNER',
}

export const bannerTypeOptions = [
  { name: 'Popup Bienvenue', _id: BannerType.WELCOME_POPUP },
  { name: 'Bannière Principale', _id: BannerType.MAIN_BANNER },
  { name: 'Publicité Audio Pré-leçon', _id: BannerType.AUDIO_PRE_LESSON },
  { name: 'Popup Pré-leçon Texte', _id: BannerType.TEXT_LESSON_POPUP },
  { name: 'Bannière de Leçon Texte', _id: BannerType.TEXT_LESSON_BANNER },
];

export const durationOptions = [
  { name: '5', _id: 5 },
  { name: '10', _id: 10 },
  { name: '30', _id: 30 },
  { name: '60', _id: 60 },
  { name: '90', _id: 90 },
];

export const priorityOptionsForBanner = [
  { name: '1', _id: 1 },
  { name: '2', _id: 2 },
  { name: '3', _id: 3 },
];

export const priorityOptionsForOther = [
  { name: '1', _id: 1 },
  { name: '2', _id: 2 },
  { name: '3', _id: 3 },
  { name: '4', _id: 4 },
  { name: '5', _id: 5 },
  { name: '6', _id: 6 },
  { name: '7', _id: 7 },
  { name: '8', _id: 8 },
  { name: '9', _id: 9 },
  { name: '10', _id: 10 },
];

export const useCampaigne = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CampaignFormValues>({
    resolver: yupResolver(campaignValidationSchema),
    defaultValues: {
      campaignAmount: undefined,
      campaignDuration: 0,
      campaignLink: '',
      campaignName: '',
      campaignPriority: 0,
      campaignResource: undefined,
      campaignStartDate: undefined,
      campaignType: '',
      company: '',
    },
    mode: 'onChange',
  });

  const { data: companies, isPending: addCompanyLoading } = useGetCompanies({});
  const { mutate: createCampaign } = useCreateCampaign();
  const [campaignType, setCampaignType] = useState<Options>();
  const [company, setCompany] = useState<Options>();
  const [duration, setDuration] = useState<Options>();
  const [campaignPriority, setCampaignPriority] = useState<Options>();
  const [generateUrlLoading, setGenerateUrlLoading] = useState<boolean>(false);

  const { isOpenCampaignModal, openCampaignModal } = useCampaignStore();
  const { showToast } = useToast();
  const { formatDate } = useFormatDate();

  const handleChangeCampaignType = (values: Options) => {
    setCampaignType(values);
  };
  const handleChangeCompany = (values: Options) => {
    setCompany(values);
  };
  const handleChangeDuration = (values: Options) => {
    setDuration(values);
  };
  const handleChangePriority = (values: Options) => {
    setCampaignPriority(values);
  };

  const onSubmit = async (data: CampaignFormValues) => {
    try {
      const token = localStorage.getItem('authToken') ?? '';
      setGenerateUrlLoading(true);

      const fileUploadPromise = data.campaignResource
        ? axios(createFile(data.campaignResource, token)).then((response) => {
            if (response.status !== 201) {
              setGenerateUrlLoading(false);
              throw new Error("Erreur lors de l'ajout du fichier");
            }
            setGenerateUrlLoading(false);
            return response.data?.publicUrl || response.data;
          })
        : Promise.resolve(null);

      const fileUrl = await fileUploadPromise;

      createCampaign(
        {
          campaignAmount: data.campaignAmount,
          campaignDuration: data.campaignDuration,
          campaignLink: data.campaignLink,
          campaignName: data.campaignName,
          campaignPriority: data.campaignPriority,
          campaignResource: fileUrl,
          campaignStartDate: data.campaignStartDate.toISOString(),
          campaignType: data.campaignType as CampaignType,
          company: data.company,
        },
        {
          onSuccess: () => {
            setGenerateUrlLoading(false);
            showToast('Campagne créée', 'success', {
              autoClose: 5000,
              position: 'top-right',
            });
            openCampaignModal();
            reset();
          },
          onError: (error) => {
            setGenerateUrlLoading(false);
            showToast(error?.message, 'error', {
              autoClose: 5000,
              position: 'top-right',
            });
          },
        },
      );
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCloseModal = () => {
    setGenerateUrlLoading(false);
    openCampaignModal();
    reset();
  };

  const priorityOptions =
    campaignType?._id === BannerType.MAIN_BANNER
      ? priorityOptionsForBanner
      : priorityOptionsForOther;

  return {
    bannerTypeOptions,
    durationOptions,
    companies,
    createCampaign,
    campaignType,
    company,
    duration,
    campaignPriority,
    setCampaignType,
    setCompany,
    setDuration,
    setCampaignPriority,
    handleChangeCampaignType,
    handleChangeCompany,
    handleChangeDuration,
    handleChangePriority,
    priorityOptions,
    onSubmit,
    isOpenCampaignModal,
    openCampaignModal,
    register,
    handleSubmit,
    setValue,
    errors,
    addCompanyLoading,
    handleCloseModal,
    generateUrlLoading,
  };
};
